import type { ChangeEvent } from 'react';
import React, { Fragment, useCallback } from 'react';
import type { Nullable } from 'corgi-types';

import { Flex } from 'common/components/Flex';
import { Radio } from 'common/components/Radio';
import { HeadlineH8 } from 'common/components/Typography';
import VisuallyHidden from 'common/components/VisuallyHidden';

interface RadioItem {
  label: string;
  value: string;
  disabled?: boolean;
  selected?: boolean;
}

interface RadioGroup {
  id?: string;
  title?: string;
  radios: RadioItem[];
}

export interface RadioGroupProps {
  name: string;
  legend: string;
  currentValue: Nullable<string>;
  groups: RadioGroup[];
  onChange: (selectedInput: string) => void;
}

const RadioGroup = ({ name, legend, currentValue, groups, onChange }: RadioGroupProps) => {
  const handleInputChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      onChange(value);
    },
    [onChange]
  );

  return (
    <Flex
      mt="sp_0"
      mx="sp_0"
      mb="sp_8"
      p="sp_0"
      as="fieldset"
      border="none"
      flexDirection="column"
      data-testid="radio-group"
    >
      {legend && <VisuallyHidden as="legend">{legend}</VisuallyHidden>}

      {groups.map(({ title, radios }) => (
        <Fragment key={`radio-group-${title}`}>
          {title && (
            <HeadlineH8 as="h3" mb="sp_8" data-testid="radio-group-heading">
              {title}
            </HeadlineH8>
          )}
          {radios.map(({ label, value, disabled }, radioIndex) => (
            <Radio
              data-testid={`radio-input-${radioIndex}`}
              key={value}
              value={value}
              name={name}
              label={label}
              onChange={handleInputChange}
              defaultChecked={currentValue === value}
              disabled={disabled}
              customIconStyles={{ ml: 'sp_0' }}
              customLabelStyles={{
                mr: 'sp_0',
                py: 'sp_4',
              }}
            />
          ))}
        </Fragment>
      ))}
    </Flex>
  );
};

export default RadioGroup;
